import { useCallback, useEffect, useMemo, useState } from "react";
import clsx from "clsx";
import { Zap } from "lucide-react";
import { api } from "@/lib/apiClient";
import { formatIsoDateShort } from "@/lib/bookingDates";
import {
  computeBillableElectricityKwh,
  electricityModeNeedsKwh,
  type ElectricityBillingMode,
} from "@/lib/electricityUsage";
import { formatCurrency } from "@/lib/money";
import { hasNotInvoicedStayAmount, stayPaymentGap } from "@/lib/stayPaymentGap";
import { StayPaymentBreakdown } from "@/components/bookings/StayPaymentBreakdown";
import {
  bookingDraftInvoiceAmount,
  hasBookingDraftInvoice,
} from "@/lib/bookingInvoiceStatus";
import { hasMissingInvoice, hasOpenInvoiceAmount } from "@/lib/bookingInvoiceDisplay";
import { canWriteInvoice } from "@/lib/permissions";
import { isInvoiceFinalized } from "@/lib/invoiceStatus";
import { InvoiceDocumentActions } from "@/components/invoices/InvoiceDocumentActions";
import { InvoiceTypeBadge } from "@/components/invoices/InvoiceTypeBadge";
import { CreateCreditNoteModal } from "@/components/invoices/CreateCreditNoteModal";
import { InvoiceCreditPayoutSection } from "@/components/invoices/InvoiceCreditPayoutSection";
import { InvoicePaymentsSection } from "@/components/invoices/InvoicePaymentsSection";
import { GuestStaySummary } from "@/components/bookings/GuestStaySummary";
import { Button } from "@/components/ui/Button";
import { StatusBadge } from "@/components/ui/StatusBadge";
import type {
  BookingDeparture,
  Discount,
  Guest,
  Invoice,
  InvoiceListResponse,
  Pitch,
} from "@/types/camping";
import type { InvoiceType } from "@/lib/invoiceType";

interface TenantBillingSettings {
  electricity_billing: {
    mode: ElectricityBillingMode;
    included_kwh_per_night: string | null;
  };
}

export interface DepartureGuestCheckoutDetailProps {
  booking: BookingDeparture;
  guest: Guest | null;
  pitch: Pitch | null;
  canEdit: boolean;
  checkoutStrict: boolean;
  userGroups: string[];
  checkOutLoading?: boolean;
  onCheckOut?: () => void;
  onChanged?: () => void;
  /** Nach Anlegen eines Rechnungsentwurfs (z. B. Entwurf-Modal öffnen). */
  onInvoiceDraftOpened?: (invoiceId: string) => void;
}

export function DepartureGuestCheckoutDetail({
  booking,
  guest,
  pitch,
  canEdit,
  checkoutStrict,
  userGroups,
  checkOutLoading,
  onCheckOut,
  onChanged,
  onInvoiceDraftOpened,
}: DepartureGuestCheckoutDetailProps) {
  const canInvoice = canWriteInvoice(userGroups);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [invoicesReady, setInvoicesReady] = useState(false);
  const [action, setAction] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [billingMode, setBillingMode] = useState<ElectricityBillingMode>("flat_per_night");
  const [includedKwhPerNight, setIncludedKwhPerNight] = useState<number | null>(null);
  const [electricityKwhInput, setElectricityKwhInput] = useState(
    booking.electricity_kwh != null ? String(booking.electricity_kwh) : "",
  );
  const [discounts, setDiscounts] = useState<Discount[]>([]);
  const [selectedDiscountId, setSelectedDiscountId] = useState("");
  const [creditNoteSource, setCreditNoteSource] = useState<Invoice | null>(null);

  const loadInvoices = useCallback(() => {
    setLoading(true);
    setInvoicesReady(false);
    setError(null);
    api
      .get<InvoiceListResponse>(`/api/v1/invoices?booking_id=${booking.id}`)
      .then((res) => {
        setInvoices(res.items);
        setInvoicesReady(true);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [booking.id]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  useEffect(() => {
    api
      .get<TenantBillingSettings>("/api/v1/settings")
      .then((s) => {
        setBillingMode(s.electricity_billing.mode);
        setIncludedKwhPerNight(
          s.electricity_billing.included_kwh_per_night != null
            ? Number(s.electricity_billing.included_kwh_per_night)
            : null,
        );
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!canInvoice) return;
    api
      .get<Discount[]>("/api/v1/discounts")
      .then(setDiscounts)
      .catch(() => setDiscounts([]));
  }, [canInvoice]);

  useEffect(() => {
    setElectricityKwhInput(
      booking.electricity_kwh != null ? String(booking.electricity_kwh) : "",
    );
  }, [booking.electricity_kwh, booking.id]);

  const openAmount = Number(booking.open_invoice_amount);
  const hasUnpaid = hasOpenInvoiceAmount(booking) || openAmount > 0;
  const hasDraftInvoice = hasBookingDraftInvoice(booking);
  const draftAmount = bookingDraftInvoiceAmount(booking);
  const checkoutBlocked = checkoutStrict && hasUnpaid;

  const activeInvoices = useMemo(
    () => invoices.filter((inv) => inv.status !== "cancelled"),
    [invoices],
  );
  const hasNoInvoice = loading
    ? hasMissingInvoice(booking)
    : activeInvoices.length === 0 && !hasDraftInvoice;
  const paymentGap = invoicesReady ? stayPaymentGap(booking, invoices) : null;
  const notInvoiced = hasNotInvoicedStayAmount(paymentGap);

  const parsedKwh = electricityKwhInput.trim() ? Number(electricityKwhInput) : null;
  const billableKwh = useMemo(
    () =>
      booking.wants_electricity && electricityModeNeedsKwh(billingMode)
        ? computeBillableElectricityKwh(
            billingMode,
            booking.nights,
            parsedKwh != null && !Number.isNaN(parsedKwh) ? parsedKwh : null,
            includedKwhPerNight,
          )
        : null,
    [billingMode, booking.nights, booking.wants_electricity, includedKwhPerNight, parsedKwh],
  );

  const hasElectricityInvoice = invoices.some(
    (inv) =>
      (inv.invoice_type as InvoiceType) === "electricity" && inv.status !== "cancelled",
  );

  const needsKwhForInvoice =
    Boolean(booking.wants_electricity) &&
    (billingMode === "per_kwh" || billingMode === "flat_with_included_kwh");

  const hasDeposit = activeInvoices.some((inv) => inv.invoice_type === "deposit");
  const hasPaymentRequest =
    Number(booking.deposit_paid_amount ?? 0) > 0 ||
    Number(booking.deposit_request_amount ?? 0) > 0;
  const hasFinal = activeInvoices.some((inv) => inv.invoice_type === "final");
  const hasFull = activeInvoices.some((inv) => inv.invoice_type === "full");
  const issuedDeposit = activeInvoices.some(
    (inv) => inv.invoice_type === "deposit" && isInvoiceFinalized(inv.status),
  );
  const hasDraftOfType = (type: InvoiceType) =>
    invoices.some((inv) => inv.status === "draft" && inv.invoice_type === type);

  const canCreateDeposit =
    canInvoice &&
    !hasDeposit &&
    !hasPaymentRequest &&
    !hasFinal &&
    !hasFull &&
    !hasDraftOfType("deposit");
  const canCreateFull =
    canInvoice && !hasDeposit && !hasFinal && !hasFull && !hasDraftOfType("full");
  const canCreateFinal =
    canInvoice &&
    issuedDeposit &&
    !hasFinal &&
    !hasFull &&
    !hasDraftOfType("final");
  const canCreateAnyInvoice = canCreateDeposit || canCreateFull || canCreateFinal;

  async function createDraft(invoiceType: InvoiceType) {
    if (!canInvoice) return;
    setAction(`create-${invoiceType}`);
    setError(null);
    try {
      const payload: {
        booking_id: string;
        invoice_type: InvoiceType;
        electricity_kwh?: string;
        discount_id?: string;
      } = {
        booking_id: booking.id,
        invoice_type: invoiceType,
      };
      if (needsKwhForInvoice) {
        const kwh = electricityKwhInput.trim();
        if (!kwh) {
          setError("Bitte den Stromverbrauch in kWh eingeben.");
          return;
        }
        payload.electricity_kwh = kwh;
      }
      if (
        selectedDiscountId &&
        (invoiceType === "full" || invoiceType === "final")
      ) {
        payload.discount_id = selectedDiscountId;
      }
      const draft = await api.post<Invoice>("/api/v1/invoices/from-booking", payload);
      loadInvoices();
      onChanged?.();
      onInvoiceDraftOpened?.(draft.id);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Rechnungsentwurf konnte nicht erstellt werden.",
      );
    } finally {
      setAction(null);
    }
  }

  async function issueInvoice(invoiceId: string) {
    if (!canInvoice) return;
    setAction(`issue-${invoiceId}`);
    setError(null);
    try {
      await api.post<Invoice>(`/api/v1/invoices/${invoiceId}/issue`, {});
      loadInvoices();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Rechnung konnte nicht ausgestellt werden.");
    } finally {
      setAction(null);
    }
  }

  const includedTotal =
    billingMode === "flat_with_included_kwh" && includedKwhPerNight != null
      ? includedKwhPerNight * booking.nights
      : null;

  async function createAndSendElectricityInvoice() {
    if (!canInvoice) return;
    const kwh = electricityKwhInput.trim();
    if (!kwh) {
      setError("Bitte den Stromverbrauch in kWh eingeben.");
      return;
    }
    setAction("electricity");
    setError(null);
    try {
      const draft = await api.post<Invoice>(
        "/api/v1/invoices/electricity-supplement-from-booking",
        { booking_id: booking.id, electricity_kwh: kwh },
      );
      const issued = await api.post<Invoice>(`/api/v1/invoices/${draft.id}/issue`, {});
      if (guest?.email?.trim()) {
        await api.post(`/api/v1/invoices/${issued.id}/send-email`, {});
      }
      loadInvoices();
      onChanged?.();
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Strom-Nachzahlung konnte nicht erstellt werden.",
      );
    } finally {
      setAction(null);
    }
  }

  return (
    <div className="space-y-5">
      <CreateCreditNoteModal
        open={creditNoteSource != null}
        sourceInvoice={creditNoteSource}
        onClose={() => setCreditNoteSource(null)}
        onCreated={() => {
          loadInvoices();
          onChanged?.();
        }}
      />
      <div
        className={clsx(
          "flex flex-wrap items-center gap-x-3 gap-y-2 rounded-xl border px-4 py-3",
          booking.is_departure_overdue
            ? "border-status-red/40 bg-status-red/10"
            : loading
              ? "border-separator bg-fill-tertiary/30"
              : hasUnpaid || hasNoInvoice || hasDraftInvoice || notInvoiced
                ? "border-status-orange/35 bg-status-orange/10"
                : "border-status-green/30 bg-status-green/10",
        )}
      >
        <p className="text-sm font-semibold text-label">
          {booking.is_departure_overdue ? (
            <span className="text-status-red">Abreise überfällig</span>
          ) : (
            "Abreise"
          )}
          <span className="ml-2 font-medium text-label-secondary">
            {formatIsoDateShort(booking.departure_date)}
          </span>
        </p>
        <p
          className={clsx(
            "text-sm font-semibold sm:ml-auto",
            loading
              ? "text-label-secondary"
              : hasUnpaid || hasNoInvoice || hasDraftInvoice || notInvoiced
              ? "text-status-orange"
              : "text-status-green",
          )}
        >
          {loading
            ? "Zahlungsstand wird geladen…"
            : hasUnpaid
            ? `Offen ${formatCurrency(booking.open_invoice_amount, booking.currency)}${
                notInvoiced && paymentGap
                  ? ` · noch nicht berechnet ${formatCurrency(paymentGap.notInvoicedAmount, paymentGap.currency)}`
                  : ""
              }`
            : notInvoiced && paymentGap
              ? `Noch nicht berechnet ${formatCurrency(paymentGap.notInvoicedAmount, paymentGap.currency)}`
            : hasDraftInvoice
              ? `Entwurf${
                  draftAmount > 0
                    ? ` ${formatCurrency(booking.draft_invoice_amount!, booking.currency)}`
                    : ""
                }`
              : hasNoInvoice
                ? "Keine Rechnung"
                : paymentGap?.stayInvoiced
                  ? "Bezahlt"
                  : "Keine offene Rechnung"}
        </p>
      </div>

      <GuestStaySummary booking={booking} guest={guest} pitch={pitch} mode="checkout" />

      {booking.wants_electricity ? (
        <section className="space-y-3 rounded-xl border border-separator bg-fill-tertiary/30 p-4">
          <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-label-secondary">
            <Zap className="h-4 w-4" aria-hidden />
            Stromverbrauch
          </h3>
          {electricityModeNeedsKwh(billingMode) ? (
            <>
              <label className="flex max-w-xs flex-col gap-1 text-sm">
                Verbrauch (kWh)
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  className="eh-input"
                  value={electricityKwhInput}
                  onChange={(e) => setElectricityKwhInput(e.target.value)}
                  disabled={!canInvoice || !!action}
                />
              </label>
              {includedTotal != null ? (
                <p className="text-xs text-label-secondary">
                  Inkludiert: {includedTotal.toFixed(3)} kWh ({booking.nights} N. ×{" "}
                  {includedKwhPerNight} kWh)
                </p>
              ) : null}
              {billableKwh != null ? (
                <p className="text-sm font-medium text-status-amber">
                  Nachzuzahlender Verbrauch: {billableKwh.toFixed(3)} kWh
                </p>
              ) : parsedKwh != null && !Number.isNaN(parsedKwh) ? (
                <p className="text-sm text-label-secondary">
                  Kein Mehrverbrauch über die inkludierte Menge.
                </p>
              ) : (
                <p className="text-xs text-label-tertiary">
                  kWh erfassen, um eine mögliche Nachzahlung zu prüfen.
                </p>
              )}
              {canInvoice && billableKwh != null && !hasElectricityInvoice ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  loading={action === "electricity"}
                  disabled={!!action}
                  onClick={() => void createAndSendElectricityInvoice()}
                >
                  Strom-Nachzahlung erstellen und an Gast senden
                </Button>
              ) : null}
              {hasElectricityInvoice ? (
                <p className="text-xs text-label-secondary">
                  Es existiert bereits eine Strom-Nachzahlungsrechnung zu dieser Buchung.
                </p>
              ) : null}
              {!guest?.email?.trim() && billableKwh != null && !hasElectricityInvoice ? (
                <p className="text-xs text-status-amber">
                  Gast hat keine E-Mail – die Rechnung wird erstellt und ausgestellt, aber nicht
                  versendet.
                </p>
              ) : null}
            </>
          ) : (
            <p className="text-sm text-label-secondary">
              Strom wird pauschal pro Nacht abgerechnet (Einstellung „Strompauschale“).
            </p>
          )}
        </section>
      ) : null}

      <section className="space-y-3 rounded-xl border border-separator p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-label">Rechnung</h3>
            <p className="mt-0.5 text-sm text-label-secondary">
              {loading
                ? "Rechnungen werden geladen…"
                : notInvoiced
                  ? "Ein Teil des Aufenthalts ist noch nicht in Rechnung gestellt."
                : hasNoInvoice
                  ? "Für diesen Aufenthalt liegt noch keine Rechnung vor."
                  : "Zahlungen und Belege zu diesem Aufenthalt."}
            </p>
          </div>
          {canCreateAnyInvoice ? (
            <div className="flex flex-wrap gap-2">
              {canCreateFull ? (
                <Button
                  type="button"
                  size="sm"
                  loading={action === "create-full"}
                  disabled={!!action}
                  onClick={() => void createDraft("full")}
                >
                  Vollrechnung erstellen
                </Button>
              ) : null}
              {canCreateFinal ? (
                <Button
                  type="button"
                  size="sm"
                  loading={action === "create-final"}
                  disabled={!!action}
                  onClick={() => void createDraft("final")}
                >
                  Schlussrechnung erstellen
                </Button>
              ) : null}
              {canCreateDeposit ? (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  loading={action === "create-deposit"}
                  disabled={!!action}
                  onClick={() => void createDraft("deposit")}
                >
                  Anzahlung erstellen
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
        {paymentGap ? <StayPaymentBreakdown gap={paymentGap} /> : null}
        {canInvoice && (canCreateFull || canCreateFinal) ? (
          <label className="flex max-w-xs flex-col gap-1 text-xs font-medium text-label-secondary">
            Rabatt
            <select
              className="eh-input"
              value={selectedDiscountId}
              onChange={(e) => setSelectedDiscountId(e.target.value)}
              disabled={!!action}
            >
              <option value="">Kein Rabatt</option>
              {discounts.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {d.discount_type === "fixed" ? ` (${d.value} EUR)` : ` (${d.value} %)`}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {loading ? null : invoices.length === 0 ? null : (
          <ul className="space-y-3">
            {invoices.map((inv) => {
              const isCreditNote = inv.invoice_type === "credit_note";
              const paidOnInvoice = Number(inv.paid_amount ?? 0);
              const canCreateCreditNote =
                canInvoice &&
                !isCreditNote &&
                isInvoiceFinalized(inv.status) &&
                Number.isFinite(paidOnInvoice) &&
                paidOnInvoice > 0;
              return (
              <li
                key={inv.id}
                className="rounded-xl border border-separator bg-surface px-3 py-3 text-sm"
              >
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-mono text-xs text-label-secondary">
                    {inv.invoice_number}
                  </span>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <InvoiceTypeBadge invoiceType={inv.invoice_type as InvoiceType} />
                    <StatusBadge status={inv.status} kind="invoice" />
                  </div>
                </div>
                <p className="mt-1 tabular-nums text-label">
                  {formatCurrency(inv.total_amount, inv.currency)}
                  {inv.open_amount != null && isInvoiceFinalized(inv.status) ? (
                    <span className="ml-2 text-label-secondary">
                      ({isCreditNote ? "auszuzahlen" : "offen"}:{" "}
                      {formatCurrency(inv.open_amount, inv.currency)})
                    </span>
                  ) : null}
                </p>
                <InvoiceDocumentActions
                  invoiceId={inv.id}
                  invoiceNumber={inv.invoice_number}
                  status={inv.status}
                  disabled={!!action}
                />
                {canInvoice && inv.status === "draft" ? (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      loading={action === `issue-${inv.id}`}
                      disabled={!!action}
                      onClick={() => void issueInvoice(inv.id)}
                    >
                      Rechnung ausstellen
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={!!action}
                      onClick={() => onInvoiceDraftOpened?.(inv.id)}
                    >
                      Entwurf bearbeiten
                    </Button>
                  </div>
                ) : null}
                {canCreateCreditNote ? (
                  <div className="mt-3">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      disabled={!!action}
                      onClick={() => setCreditNoteSource(inv)}
                    >
                      Gutschrift erstellen
                    </Button>
                  </div>
                ) : null}
                {isCreditNote ? (
                  <InvoiceCreditPayoutSection
                    invoice={inv}
                    canRecord={canInvoice}
                    onPayoutRecorded={() => {
                      loadInvoices();
                      onChanged?.();
                    }}
                  />
                ) : (
                  <InvoicePaymentsSection
                    invoice={inv}
                    canRecord={canInvoice}
                    onPaymentRecorded={() => {
                      loadInvoices();
                      onChanged?.();
                    }}
                  />
                )}
              </li>
            );
            })}
          </ul>
        )}
      </section>

      {hasDraftInvoice && onInvoiceDraftOpened ? (
        <div className="space-y-3 rounded-xl border border-status-amber/30 bg-status-amber/5 p-4">
          <p className="text-sm text-status-amber">
            Es gibt einen offenen Rechnungsentwurf. Stellen Sie die Rechnung aus und erfassen Sie
            ggf. die Zahlung, bevor Sie den Check-out abschließen.
          </p>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onInvoiceDraftOpened(booking.draft_invoice_id!)}
          >
            Rechnung fortsetzen
          </Button>
        </div>
      ) : null}

      {!hasUnpaid && hasNoInvoice && !canInvoice ? (
        <p className="text-xs text-label-secondary">
          Zum Anlegen von Rechnungen ist die Berechtigung „Rechnungen“ erforderlich.
        </p>
      ) : null}

      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      {canEdit && onCheckOut && booking.status === "checked_in" ? (
        <div className="sticky bottom-0 z-10 flex flex-col gap-3 border-t border-separator bg-card/95 py-3 backdrop-blur-sm sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-label-secondary">
            {checkoutBlocked
              ? "Check-out ist gesperrt, solange Rechnungen offen sind."
              : hasNoInvoice
                ? "Noch keine Rechnung. Der Check-out ist trotzdem möglich."
                : "Aufenthalt abschließen und den Stellplatz freigeben."}
          </p>
          <Button
            type="button"
            className="shrink-0"
            loading={checkOutLoading}
            disabled={checkoutBlocked}
            onClick={onCheckOut}
          >
            Check-out
          </Button>
        </div>
      ) : null}
    </div>
  );
}
